import { Shape } from '@/services/shape';
import {
  addResourceIfNotExists,
  generateLayoutAst,
  insertImages
} from '../utils/resource.utils';
import { shapesToLayoutAst } from '.';
import { buildAstState } from '..';
import { getRefName } from '../utils/name.utils';
import { addWidgetTranslationIfNeed } from '../utils/translation.utils';
import { canUse } from '@/utils/can-use.utils';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { useLayerStore } from '@/store/use-layer-store';
import { matchWidgetContainer } from '@/resources/widget-display-container-rules';

const _insertEmptyWidget = (resourceAst: Ast, slotAst: Ast, shape: Shape) => {
  const imageAst: Ast = {
    tag: 'Image',
    attrsMap: {
      src: shape.attrs.emptyImageSrc ?? '',
      compressMethod: buildAstState.data.compressMethod,
      format: buildAstState.data.imageFormatDefault
    },
    children: []
  };
  const imageAst2 = addResourceIfNotExists(resourceAst, imageAst);

  const firstWidget = shape.children[shape.children.length - 1];
  // Q63/Q61 容器空占位使用"不显示"，其他设备使用"无"
  const deviceType = useWatchfaceStore().deviceType;
  const isNotDisplayDevice = ['Q63', 'Q61'].includes(deviceType);
  const emptyTranslationKey = isNotDisplayDevice
    ? 'not_display.watchface'
    : 'none';
  const widgetAst: Ast = {
    tag: 'Widget',
    attrsMap: {
      widgetName: isNotDisplayDevice ? '不显示' : '无',
      groupType: 'none',
      dataKey: 'none',
      w: String(firstWidget?.attrs.w ?? '100'),
      h: String(firstWidget?.attrs.h ?? '100')
    },
    children: [
      {
        tag: 'Item',
        attrsMap: {
          x: '0',
          y: '0',
          ref: '@' + imageAst2.attrsMap.name
        },
        children: []
      }
    ]
  };
  // 添加翻译
  addWidgetTranslationIfNeed(resourceAst, widgetAst, emptyTranslationKey);
  // 添加editbox属性
  const imageMap = insertImages(
    resourceAst,
    {
      editBox: shape.attrs.resources?.editBox
    },
    {
      recolorEnable: true
    }
  );
  // 只有当 editBox 资源存在时才设置该属性，避免生成无效的 UID
  if (imageMap['editBox']) {
    widgetAst.attrsMap['editBox'] = getRefName(imageMap['editBox']);
  }
  // 添加widget预览图,预览图和引用的图一致
  widgetAst.attrsMap['preview'] = getRefName(imageAst2.attrsMap.name);
  const widgetAst2 = addResourceIfNotExists(resourceAst, widgetAst);
  if (canUse('none-widget-last').valid)
    slotAst.children.push({
      tag: 'Item',
      attrsMap: {
        ref: '@' + widgetAst2.attrsMap.name,
        _id: 'none'
      },
      children: []
    });
  else
    slotAst.children.unshift({
      tag: 'Item',
      attrsMap: {
        ref: '@' + widgetAst2.attrsMap.name,
        _id: 'none'
      },
      children: []
    });
};

/**
 * 返回类型为slot widget group的ast数据
 * @param resourceAst
 * @param shape
 * @returns
 */
export const getSlotAst = (
  resourceAst: Ast,
  shape: Shape,
  layer: StoreLayerItem
) => {
  const watchStore = useWatchfaceStore();
  const layerStore = useLayerStore();
  const { slotType, widgetIdToDisplay = '', x = 0, y = 0 } = shape.attrs;
  const ast: Ast = {
    tag: 'Slot',
    attrsMap: {
      type: slotType ? slotType : 'widget'
    },
    children: []
  };

  // 根据容器宽高匹配展示容器类型，设置 slotShape 属性
  const containerType = matchWidgetContainer(
    shape.attrs.w ?? 0,
    shape.attrs.h ?? 0
  );
  if (containerType) {
    ast.attrsMap.slotShape = containerType;
  }

  const layerChildren = layer.children ?? [];
  const itemAsts = shapesToLayoutAst(resourceAst, [...layerChildren], layer);
  // 重置tag（原tag为layout）
  itemAsts.forEach((item) => {
    item.tag = 'Item';
    // 容器下的item没有x y坐标信息
    delete item.attrsMap.x;
    delete item.attrsMap.y;
  });
  ast.children = [...itemAsts];

  // 需求变更：为了和上表后widget保持顺序一致，需要重置widgets
  ast.children?.reverse();

  if (shape.attrs.slotType == 'widget') {
    // 如果可选无，则插入一个空的widget
    if (shape.attrs.includeEmpty) {
      _insertEmptyWidget(resourceAst, ast, shape);
    }
    // slot-default-display 支持时（Q61/Q63内部版）：保持原始顺序，
    // 默认展示项通过 layout 的 parameter 属性传递（在下方生成 layout 时处理）。
    // 不支持时：沿用旧逻辑，将默认展示项移到第一个
    if (widgetIdToDisplay && !canUse('slot-default-display').valid) {
      const _firstWidget = ast.children.find(
        (item) => item.attrsMap._id == widgetIdToDisplay
      );
      if (_firstWidget) {
        ast.children = ast.children.filter(
          (item) => item.attrsMap._id != widgetIdToDisplay
        );
        ast.children.unshift(_firstWidget);
      }
    }

    // 自定义位置
    if (shape.attrs.movable && shape.attrs.positions?.length) {
      ast.attrsMap.movable = 'true';
      const positions = shape.attrs.positions;
      positions.forEach((position, index) => {
        ast.children.push({
          tag: 'Position',
          attrsMap: {
            x: String(position.x ?? 0),
            y: String(position.y ?? 0),
            _curSlotPosition:
              shape.attrs.curSlotPosition == index ? 'true' : 'false'
          },
          children: []
        });
      });
    }
  }
  // 检查是否支持slot跟随功能且watchStore中有slotFollowing配置
  // 自定义位置(movable/positions)独立于容器跟随，有自定义位置时不设置 SlotGroupName
  if (canUse('support-slot-following').valid && watchStore.slotFollowing) {
    const { slotFollowingId, followingGroupName } = shape.attrs;
    const hasCustomPosition =
      shape.attrs.movable && shape.attrs.positions?.length;
    // 获取所有主题中的Slot类型子元素
    const allSlots = layerStore.themes.flatMap((theme) =>
      theme.children.filter((child) => child.type === 'Slot')
    );

    // 如果存在要跟随的slot ID，且无自定义位置时才设置 SlotGroupName（自定义位置独立，不跟随父级）
    if (slotFollowingId && !hasCustomPosition) {
      // 在所有slot中查找匹配ID的slot
      const followingSlot = allSlots.find(
        (slot) => slot.id === slotFollowingId
      );
      // 如果找到匹配的slot，设置当前ast的SlotGroupName属性为跟随slot的followingGroupName（默认为空字符串）
      if (followingSlot)
        ast.attrsMap.SlotGroupName =
          followingSlot?.attrs.followingGroupName ?? '';
    }
    // 如果存在followingGroupName
    else if (followingGroupName) {
      // 检查是否有其他slot正在跟随当前shape
      if (allSlots.find((slot) => slot.attrs.slotFollowingId === shape.id)) {
        // 如果有，设置当前ast的SlotGroupName为指定的followingGroupName
        ast.attrsMap.SlotGroupName = followingGroupName;
      } else {
        // 如果没有，设置SlotGroupName为'none'
        ast.attrsMap.SlotGroupName = 'none';
      }
    }
  }

  const _ast = addResourceIfNotExists(resourceAst, ast);
  const layoutAst: Ast = generateLayoutAst({
    ref: '@' + _ast.attrsMap.name,
    x: x ?? 0,
    y: y ?? 0
  });

  // 在layout中通过parameter传递默认展示项的widget ref
  // 仅 slot-default-display 支持的设备（Q61/Q63内部版）写入，
  // 不支持时默认项已 unshift 到第一个，无需 parameter
  if (
    widgetIdToDisplay &&
    shape.attrs.slotType == 'widget' &&
    canUse('slot-default-display').valid
  ) {
    const defaultIndex = ast.children.findIndex(
      (item) => item.attrsMap._id == widgetIdToDisplay
    );
    if (defaultIndex >= 0) {
      const defaultChild = ast.children[defaultIndex];
      layoutAst.attrsMap.parameter = defaultChild.attrsMap.ref;
    }
  }

  return layoutAst;
};
